import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Mail } from 'lucide-react';

// Step 1 of the Add/Edit dialog: address, contact, bank details, courts, etc.
export default function LocationFormDetailsTab({ formData, onUpdateField }) {
  const set = (field) => (e) => onUpdateField(field, e.target.value);

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
          Location Name *
        </Label>
        <Input
          data-testid="location-name-input"
          value={formData.name}
          onChange={set('name')}
          placeholder="e.g., Downtown Sports Center"
          className="h-12 rounded-2xl"
        />
      </div>

      <div className="col-span-2">
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
          Street Address *
        </Label>
        <Input
          data-testid="location-address-input"
          value={formData.address}
          onChange={set('address')}
          placeholder="123 Main Street"
          className="h-12 rounded-2xl"
        />
      </div>

      <div>
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">City</Label>
        <Input value={formData.city} onChange={set('city')} placeholder="City" className="h-12 rounded-2xl" />
      </div>
      <div>
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Postal Code</Label>
        <Input value={formData.postal_code} onChange={set('postal_code')} placeholder="12345" className="h-12 rounded-2xl" />
      </div>
      <div>
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Phone</Label>
        <Input value={formData.phone} onChange={set('phone')} placeholder="+673 XXX XXXX" className="h-12 rounded-2xl" />
      </div>
      <div>
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Email</Label>
        <Input type="email" value={formData.email} onChange={set('email')} placeholder="location@example.com" className="h-12 rounded-2xl" />
      </div>

      <div>
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Staff Notification Email
        </Label>
        <Input
          type="text"
          value={formData.notification_email}
          onChange={set('notification_email')}
          placeholder="staff@example.com, ops@example.com (optional)"
          className="h-12 rounded-2xl"
          data-testid="location-notification-email-input"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Comma-separated to notify multiple staff. e.g.{' '}
          <span className="font-mono">andy@x.com, ops@x.com</span> — max 10 recipients.
        </p>
      </div>

      <div>
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
          Bank Name (optional)
        </Label>
        <Input
          type="text"
          value={formData.bank_name}
          onChange={set('bank_name')}
          placeholder="e.g. BIBD"
          className="h-12 rounded-2xl"
          data-testid="location-bank-name-input"
        />
      </div>

      <div>
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
          Bank Account Number (optional)
        </Label>
        <Input
          type="text"
          value={formData.bank_account}
          onChange={set('bank_account')}
          placeholder="e.g. 00-001-01-234567-8"
          className="h-12 rounded-2xl"
          data-testid="location-bank-account-input"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Shown to customers in "Payment Required" emails for held bookings.
        </p>
      </div>

      <div>
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
          Number of Courts
        </Label>
        <Input
          type="number"
          min="1"
          max="20"
          value={formData.courts_count}
          onChange={(e) => onUpdateField('courts_count', parseInt(e.target.value, 10) || 1)}
          className="h-12 rounded-2xl"
        />
      </div>

      <div className="flex items-center gap-3">
        <Label className="uppercase text-xs font-bold text-muted-foreground">Active</Label>
        <Switch
          checked={formData.active}
          onCheckedChange={(checked) => onUpdateField('active', checked)}
        />
      </div>

      <div className="col-span-2">
        <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">Description</Label>
        <Textarea
          value={formData.description}
          onChange={set('description')}
          placeholder="Brief description of this location..."
          className="rounded-2xl min-h-[100px]"
        />
      </div>
    </div>
  );
}
